import React, { useCallback } from 'react';
import { Modal } from "react-bootstrap";
import { BsXCircleFill } from 'react-icons/bs';
import MaterialIcon from "../../../components/MaterialIcon";
import Carousel from "react-multi-carousel";
import { BASE_URL, buildImageUrl } from "../../../helper/constants";
import { ARTICLE, store } from "../../../store";
import { useSelector } from 'react-redux';
import "./FeedViewDialog.css";

// Helper function to get image URL from various data formats
const getImageUrl = (image) => {
    if (!image) return '';
    
    // If it's a local file with url property
    if (image.local && image.url) {
        return image.url;
    }
    
    // If url property exists and is a complete URL
    if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
        return image.url;
    }
    
    // If href property exists (for embedded URLs from articles)
    if (image.href) {
        if (image.href.startsWith('http://') || image.href.startsWith('https://')) {
            return image.href;
        }
        return BASE_URL + image.href;
    }
    
    // If fileUrl exists
    if (image.fileUrl) {
        const fileUrlValue = Array.isArray(image.fileUrl) ? image.fileUrl[0] : image.fileUrl;
        if (fileUrlValue && (fileUrlValue.startsWith('http://') || fileUrlValue.startsWith('https://'))) {
            return fileUrlValue;
        }
        return buildImageUrl(false, image.fileUrl, image.attachedFileName);
    }
    
    return '';
};

const isPdfAttachmentItem = (item) => {
    if (!item) return false;
    if (item.isPdf) return true;
    if (item.type === 'application/pdf' || item.fileType === 'application/pdf') return true;
    const candidates = [item.fileUrl, item.href, item.url, item.name, item.attachedFileName];
    for (const c of candidates) {
        if (typeof c === 'string' && /\.pdf(\?|$)/i.test(c)) return true;
    }
    return false;
};

const getPdfDisplayNameFromItem = (item) => {
    if (!item) return 'document.pdf';
    if (item.name) return String(item.name).split('?')[0];
    if (item.attachedFileName) return String(item.attachedFileName).split('?')[0];
    const src = item.fileUrl || item.href || item.url || '';
    if (typeof src === 'string') {
        const last = src.split('/').pop();
        if (last) return last.split('?')[0];
    }
    return 'document.pdf';
};



export default function FeedViewDialog({ viewFeed, article: propArticle, articlesList: propArticlesList, currentArticleIndex: propCurrentIndex }) {
    // Get state directly from Redux - this will cause re-render when Redux state changes
    const reduxArticle = useSelector(state => state.article);
    const reduxArticlesList = useSelector(state => state.articlesList);
    const reduxCurrentIndex = useSelector(state => state.currentArticleIndex);
    
    // Use Redux state if available, otherwise fall back to props
    // If Redux has an article, ALWAYS use Redux values (source of truth for navigation)
    // Otherwise use props (initial load before Redux is populated)
    const article = reduxArticle || propArticle;
    
    // Determine which articlesList to use
    // Priority: Redux articlesList (if Redux has article) > propArticlesList > []
    let articlesList = [];
    if (reduxArticle) {
        // Redux has article - use Redux articlesList, fallback to propArticlesList if Redux list is empty/undefined
        articlesList = (reduxArticlesList && reduxArticlesList.length > 0) 
            ? reduxArticlesList 
            : (propArticlesList && propArticlesList.length > 0 ? propArticlesList : []);
    } else {
        // No Redux article yet - use props
        articlesList = propArticlesList || [];
    }
    
    // Determine which index to use
    const currentArticleIndex = reduxArticle 
        ? (reduxCurrentIndex !== undefined && reduxCurrentIndex !== null ? reduxCurrentIndex : 0)
        : (propCurrentIndex !== undefined ? propCurrentIndex : 0);
    
    // Use Redux values directly - no local state needed
    const currentArticle = article;
    const currentIndex = currentArticleIndex;
    
    // Calculate navigation state - must be defined before useEffects that use them
    const list = articlesList && articlesList.length > 0 ? articlesList : [];
    const canGoNext = list.length > 0 && currentIndex < list.length - 1;
    const canGoPrevious = list.length > 0 && currentIndex > 0;
    
    // Debug logging
    React.useEffect(() => {
        console.log('=== FeedViewDialog - State Update ===');
        console.log('Redux State:', {
            reduxArticle: reduxArticle?.id,
            reduxArticlesListLength: reduxArticlesList?.length,
            reduxCurrentIndex,
            reduxArticleIds: reduxArticlesList?.map(a => a?.id)
        });
        console.log('Computed Values:', {
            article: article?.id,
            articlesListLength: articlesList.length,
            currentIndex,
            currentArticleId: currentArticle?.id,
            articleIds: articlesList.map(a => a?.id)
        });
        console.log('Navigation:', {
            canGoNext,
            canGoPrevious,
            listLength: list.length
        });
        console.log('=====================================');
    }, [reduxArticle, reduxArticlesList, reduxCurrentIndex, articlesList, currentIndex, currentArticle, canGoNext, canGoPrevious, list]);

    const handleClose = () => {
        store.dispatch({
            type: ARTICLE,
            article: null,
            articlesList: [],
            currentArticleIndex: 0
        })
    }

    const handleNext = useCallback(() => {
        // Always get fresh values from Redux on each call
        const currentReduxState = store.getState();
        const list = currentReduxState.articlesList || [];
        const idx = (currentReduxState.currentArticleIndex !== undefined && currentReduxState.currentArticleIndex !== null)
            ? currentReduxState.currentArticleIndex
            : 0;
            
        console.log('handleNext called - list.length:', list.length, 'currentIndex:', idx, 'list IDs:', list.map(a => a?.id));
        if (list.length > 0 && idx < list.length - 1) {
            const nextIndex = idx + 1;
            const nextArticle = list[nextIndex];
            console.log('handleNext - nextIndex:', nextIndex, 'nextArticle:', nextArticle?.id);
            if (nextArticle) {
                // Update Redux - component will re-render automatically via useSelector
                console.log('Dispatching to Redux - article:', nextArticle.id, 'articlesList length:', list.length, 'currentArticleIndex:', nextIndex);
                store.dispatch({
                    type: ARTICLE,
                    article: nextArticle,
                    articlesList: list,
                    currentArticleIndex: nextIndex
                });
            } else {
                console.log('handleNext - nextArticle is null/undefined');
            }
        } else {
            console.log('handleNext - Cannot navigate. list.length:', list.length, 'currentIndex:', idx, 'can go next:', idx < list.length - 1);
        }
    }, []);

    const handlePrevious = useCallback(() => {
        // Always get fresh values from Redux on each call
        const currentReduxState = store.getState();
        const list = currentReduxState.articlesList || [];
        const idx = (currentReduxState.currentArticleIndex !== undefined && currentReduxState.currentArticleIndex !== null)
            ? currentReduxState.currentArticleIndex
            : 0;
            
        console.log('handlePrevious called - list.length:', list.length, 'currentIndex:', idx, 'list IDs:', list.map(a => a?.id));
        if (list.length > 0 && idx > 0) {
            const prevIndex = idx - 1;
            const prevArticle = list[prevIndex];
            console.log('handlePrevious - prevIndex:', prevIndex, 'prevArticle:', prevArticle?.id);
            if (prevArticle) {
                // Update Redux - component will re-render automatically via useSelector
                console.log('Dispatching to Redux - article:', prevArticle.id, 'articlesList length:', list.length, 'currentArticleIndex:', prevIndex);
                store.dispatch({
                    type: ARTICLE,
                    article: prevArticle,
                    articlesList: list,
                    currentArticleIndex: prevIndex
                });
            } else {
                console.log('handlePrevious - prevArticle is null/undefined');
            }
        } else {
            console.log('handlePrevious - Cannot navigate. list.length:', list.length, 'currentIndex:', idx, 'can go prev:', idx > 0);
        }
    }, []);
    
    // Debug: Log navigation state on every render
    React.useEffect(() => {
        console.log('FeedViewDialog - Navigation State:', {
            listLength: list.length,
            currentIndex,
            canGoNext,
            canGoPrevious,
            currentArticleId: currentArticle?.id,
            shouldShowNextButton: canGoNext,
            shouldShowPrevButton: canGoPrevious
        });
    }, [list.length, currentIndex, canGoNext, canGoPrevious, currentArticle]);

    if (!currentArticle) {
        return null;
    }

    return (
        <React.Fragment>
            <Modal 
                size="lg" 
                show={!!currentArticle} 
                onHide={handleClose} 
                centered 
                className="feed-dialog"
                key={`modal-${currentArticle?.id}-${currentIndex}`}
            >

                <Modal.Body className="position-relative p-0">
                    <div className="article-wrapper feed-view-dialog__wrapper">
                        {/* Article Navigation Buttons - Show if there are multiple articles */}
                        {list.length > 1 && (
                            <>
                                {/* Previous Article Button */}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🔄 Previous article button clicked');
                                        handlePrevious();
                                    }}
                                    disabled={!canGoPrevious}
                                    className={`article-nav-button article-nav-button-left feed-view-dialog__nav feed-view-dialog__nav--prev${canGoPrevious ? '' : ' feed-view-dialog__nav--disabled'}`}
                                    onMouseEnter={(e) => {
                                        if (canGoPrevious) {
                                            e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                                            e.target.style.transform = 'translateY(-50%) scale(1.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (canGoPrevious) {
                                            e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                                            e.target.style.transform = 'translateY(-50%) scale(1)';
                                        }
                                    }}
                                >
                                    <MaterialIcon icon="keyboard_arrow_left" size={24} color="white" fill={0} weight={300} grade={0} />
                                </button>
                                
                                {/* Next Article Button */}
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🔄 Next article button clicked');
                                        handleNext();
                                    }}
                                    disabled={!canGoNext}
                                    className={`article-nav-button article-nav-button-right feed-view-dialog__nav feed-view-dialog__nav--next${canGoNext ? '' : ' feed-view-dialog__nav--disabled'}`}
                                    onMouseEnter={(e) => {
                                        if (canGoNext) {
                                            e.target.style.background = 'rgba(0, 0, 0, 0.9)';
                                            e.target.style.transform = 'translateY(-50%) scale(1.1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (canGoNext) {
                                            e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                                            e.target.style.transform = 'translateY(-50%) scale(1)';
                                        }
                                    }}
                                >
                                    <MaterialIcon icon="keyboard_arrow_right" size={24} color="white" fill={0} weight={300} grade={0} />
                                </button>
                                
                                {/* Article Counter - Shows current position */}
                                {list.length > 1 && (
                                    <div className="feed-view-dialog__counter">
                                        {currentIndex + 1} / {list.length}
                                    </div>
                                )}
                            </>
                        )}
                        {/* Debug: Log uploadUrl */}
                        {console.log('FeedViewDialog - currentArticle.uploadUrl:', currentArticle?.uploadUrl, 'length:', currentArticle?.uploadUrl?.length)}
                        {
                            currentArticle && currentArticle.uploadUrl && currentArticle.uploadUrl.length > 0 &&
                            <div className="article-img feed-view-dialog__media" key={`article-wrapper-${currentArticle.id}-${currentIndex}`}>
                                <Carousel
                                    key={`carousel-${currentArticle.id}-${currentIndex}`}
                                    additionalTransfrom={0}
                                    arrows={currentArticle.uploadUrl.length > 1 ? true : false}
                                    centerMode={false}
                                    className="p-0"
                                    // containerClass="container"
                                    dotListClass=""
                                    draggable={currentArticle.uploadUrl.length > 1 ? true : false}
                                    focusOnSelect={false}
                                    infinite={false}
                                    itemClass=""
                                    keyBoardControl
                                    minimumTouchDrag={80}
                                    renderButtonGroupOutside={false}
                                    renderDotsOutside={false}
                                    responsive={{
                                        desktop: {
                                            breakpoint: {
                                                max: 3000,
                                                min: 1024
                                            },
                                            items: 1,
                                        },
                                        mobile: {
                                            breakpoint: {
                                                max: 464,
                                                min: 0
                                            },
                                            items: 1
                                        },
                                        tablet: {
                                            breakpoint: {
                                                max: 1024,
                                                min: 464
                                            },
                                            items: 1
                                        }
                                    }}
                                    showDots={currentArticle.uploadUrl.length > 1 ? true : false}
                                    sliderClass=""
                                    slidesToSlide={1}
                                    swipeable
                                >
                                    {
                                        currentArticle.uploadUrl.map((image, index) => {
                                            const imgUrl = getImageUrl(image);
                                            const isPdfSlide = isPdfAttachmentItem(image) || (typeof imgUrl === 'string' && /\.pdf(\?|$)/i.test(imgUrl));
                                            console.log('FeedViewDialog - Image URL:', imgUrl, 'Original image:', image, 'isPdf:', isPdfSlide);
                                            if (isPdfSlide) {
                                                const pdfName = getPdfDisplayNameFromItem(image);
                                                return (
                                                    <div key={index} className="feed-slide feed-view-dialog__pdf-slide">
                                                        <iframe
                                                            title={pdfName}
                                                            src={imgUrl}
                                                            className="feed-view-dialog__pdf"
                                                        />
                                                        <BsXCircleFill onClick={handleClose} size={25} className="close-preview-dialog" />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={index} className="feed-slide">
                                                    <img
                                                        src={imgUrl}
                                                        className="feed-view-dialog__slide-img"
                                                        onError={(e) => {
                                                            console.error('FeedViewDialog - Image load error:', imgUrl, image);
                                                        }}
                                                    />
                                                    <BsXCircleFill onClick={handleClose} size={25} className="close-preview-dialog" />
                                                </div>
                                            );
                                        })
                                    }
                                </Carousel>
                            </div>
                        }
                    </div>
                </Modal.Body>
            </Modal>
        </React.Fragment>
    );
}
